import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { map, startWith } from 'rxjs';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';
import { PanelHostService } from '../../../../services/panel-host.service';
import { TripFinanceService } from '../../../../services/trip-finance.service';
import { TripService } from '../../../../services/trip.service';
import {
  DonationLinkInput,
  NewDonationType,
  NewDonationWithAssignments,
} from '../../../../models/finance-models';
import { ParticipantAssignmentEditorComponent } from '../../../trip-finances/participant-assignment-editor/participant-assignment-editor.component';
import { formatDateForInput } from '../../../trip-finances/trip-finance-date.util';
import { getTripRoster } from '../../../trip-finances/trip-roster.util';
import { ContentContainerComponent } from '../../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../../ui/panel-body/panel-body.component';

interface GlDonationSnapshot {
  date: string;
  amount: number;
  name: string;
  email: string;
  type: NewDonationType;
  checknum: string;
  note: string;
  assignEnabled: number;
  links: DonationLinkInput[];
}

function donationLinksEqual(a: DonationLinkInput[], b: DonationLinkInput[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((link, index) => {
    const other = b[index];
    return link.userId === other.userId && link.amount === other.amount;
  });
}

function glDonationSnapshotsEqual(a: GlDonationSnapshot, b: GlDonationSnapshot): boolean {
  return (
    a.date === b.date &&
    a.amount === b.amount &&
    a.name === b.name &&
    a.email === b.email &&
    a.type === b.type &&
    a.checknum === b.checknum &&
    a.note === b.note &&
    a.assignEnabled === b.assignEnabled &&
    donationLinksEqual(a.links, b.links)
  );
}

@Component({
  selector: 'app-edit-gl-donation-panel',
  imports: [ReactiveFormsModule, TranslocoDirective, ParticipantAssignmentEditorComponent, ContentContainerComponent, PanelBodyComponent],
  templateUrl: './edit-gl-donation-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditGlDonationPanelComponent implements OnDestroy {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripFinanceService = inject(TripFinanceService);
  private readonly tripService = inject(TripService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);
  private readonly fb = inject(FormBuilder);

  readonly donationTypes = [
    { value: NewDonationType.Cash, labelKey: 'cash' },
    { value: NewDonationType.Check, labelKey: 'check' },
    { value: NewDonationType.Other, labelKey: 'other' },
  ];

  readonly form = this.fb.group({
    date: ['', Validators.required],
    amount: [0, [Validators.required, Validators.min(0.01)]],
    name: ['', Validators.required],
    email: [''],
    type: [NewDonationType.Cash, Validators.required],
    checknum: [''],
    note: [''],
  });

  assignEnabled = signal(0);
  links = signal<DonationLinkInput[]>([]);
  submitting = signal(false);
  isEdit = signal(false);
  recordId = signal<number | null>(null);
  private readonly initialSnapshot = signal<GlDonationSnapshot | null>(null);

  roster = computed(() => getTripRoster(this.tripService.currentTrip.value()));

  readonly amount = toSignal(
    this.form.controls.amount.valueChanges.pipe(
      startWith(this.form.controls.amount.value),
      map((value) => Number(value ?? 0)),
    ),
    { initialValue: 0 },
  );

  assignmentValid = computed(() => {
    if (!this.assignEnabled()) {
      return true;
    }
    const assigned = this.links().reduce((s, l) => s + l.amount, 0);
    return Math.abs(this.amount() - assigned) < 0.01;
  });

  constructor() {
    effect(() => {
      const panel = this.panelHost.activePanel();
      const context = this.panelHost.tripFinancePanelContext();
      if (panel === 'edit-gl-donation' && context?.mode === 'gl-donation') {
        this.isEdit.set(context.action === 'edit');
        if (context.action === 'edit' && context.record) {
          const record = context.record as NewDonationWithAssignments;
          this.recordId.set(record.id);
          this.form.patchValue({
            date: formatDateForInput(record.date),
            amount: record.amount,
            name: record.name,
            email: record.email,
            type: record.type,
            checknum: record.checknum,
            note: record.note,
          });
          this.assignEnabled.set(record.assignDonation ?? 0);
          this.links.set(record.links.map((l) => ({ userId: l.userId, amount: l.amount })));
        } else {
          this.recordId.set(null);
          this.form.reset({
            date: formatDateForInput(new Date()),
            amount: 0,
            name: '',
            email: '',
            type: NewDonationType.Cash,
            checknum: '',
            note: '',
          });
          this.assignEnabled.set(0);
          this.links.set([]);
        }
        // Do not track links/assignEnabled — otherwise assignment edits re-run this
        // effect and reset the form from context.record.
        untracked(() => this.syncDirtyBaseline());
      }
    });

    effect(() => {
      const panel = this.panelHost.activePanel();
      const context = this.panelHost.tripFinancePanelContext();
      if (panel !== 'edit-gl-donation' || context?.mode !== 'gl-donation') {
        return;
      }
      this.panelHost.registerDirtyPredicate(() => this.isDirty());
    });
  }

  ngOnDestroy(): void {
    this.panelHost.clearDirtyPredicate();
  }

  cancel(): void {
    void this.panelHost.dismiss();
  }

  save(): void {
    if (this.form.invalid || this.submitting() || !this.assignmentValid()) {
      this.form.markAllAsTouched();
      return;
    }

    const trip = this.tripService.currentTrip.value();
    if (!trip) {
      return;
    }

    const body = {
      date: this.form.value.date!,
      amount: Number(this.form.value.amount),
      name: this.form.value.name!,
      email: this.form.value.email ?? '',
      type: this.form.value.type!,
      checknum: this.form.value.checknum ?? '',
      note: this.form.value.note ?? '',
      assignDonation: this.assignEnabled(),
      links: this.links(),
    };

    this.submitting.set(true);
    const request$ =
      this.isEdit() && this.recordId()
        ? this.tripFinanceService.updateGlDonation(trip.id, this.recordId()!, body)
        : this.tripFinanceService.createGlDonation(trip.id, body);

    request$.pipe(finalize(() => this.submitting.set(false))).subscribe({
      next: async () => {
        const toast = await this.toastController.create({
          message: this.transloco.translate(
            marker(this.isEdit() ? 'trip-finances.panels.gl-donation.save-success-edit' : 'trip-finances.panels.gl-donation.save-success-add')
          ),
          duration: 4000,
          color: 'success',
        });
        await toast.present();
        this.syncDirtyBaseline();
        void this.panelHost.dismiss();
      },
      error: async () => {
        const toast = await this.toastController.create({
          message: this.transloco.translate(marker('trip-finances.panels.save-error')),
          duration: 4000,
          color: 'danger',
        });
        await toast.present();
      },
    });
  }

  private captureSnapshot(): GlDonationSnapshot {
    const raw = this.form.getRawValue();
    return {
      date: raw.date ?? '',
      amount: Number(raw.amount ?? 0),
      name: raw.name ?? '',
      email: raw.email ?? '',
      type: raw.type ?? NewDonationType.Cash,
      checknum: raw.checknum ?? '',
      note: raw.note ?? '',
      assignEnabled: this.assignEnabled(),
      links: this.links().map((link) => ({ userId: link.userId, amount: link.amount })),
    };
  }

  private isDirty(): boolean {
    const baseline = this.initialSnapshot();
    if (!baseline) {
      return false;
    }
    return !glDonationSnapshotsEqual(this.captureSnapshot(), baseline);
  }

  private syncDirtyBaseline(): void {
    this.initialSnapshot.set(this.captureSnapshot());
  }
}
